/**
 * Helpers for AskAsper `/` lookupDimensionGroups from commonConfig.
 * The design library uses each entityTypes entry as the visible label and matches
 * it to LookupItem.column_name with underscore/space/case normalization — so we
 * can pass "Brand Family" while config/API still use brand_family.
 */

export type LookupDimensionGroup = {
    id: string;
    label: string;
    entityTypes: string[];
};

/** brand_family / brand family / BrandFamily → Brand Family */
export function formatLookupEntityTypeLabel(columnName: string): string {
    return columnName
        .trim()
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Pass-through from commonConfig with entityTypes rewritten for display.
 * Returns undefined when absent/invalid so `/` keeps the legacy flat list.
 */
export function withDisplayEntityTypeLabels(raw: unknown): LookupDimensionGroup[] | undefined {
    if (!Array.isArray(raw) || raw.length === 0) return undefined;

    const groups: LookupDimensionGroup[] = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object') continue;
        const { id, label, entityTypes } = entry as Record<string, unknown>;
        if (typeof id !== 'string' || !id.trim()) continue;
        if (typeof label !== 'string' || !label.trim()) continue;
        if (!Array.isArray(entityTypes)) continue;

        const types = entityTypes
            .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
            .map(formatLookupEntityTypeLabel);
        if (types.length === 0) continue;

        groups.push({ id: id.trim(), label: label.trim(), entityTypes: types });
    }
    return groups.length > 0 ? groups : undefined;
}
